import { Search } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

const DEBOUNCE_MS = 300

interface SearchFieldProps {
  /** The search term in the URL. */
  value: string
  /** Called (debounced) with the trimmed term; '' clears the search. Keep it stable: a new function per render restarts the debounce. */
  onSearch: (term: string) => void
  placeholder: string
  /** The accessible name of the box, e.g. "Zoek contacten". */
  label: string
  /** For the wrapper: the width of the box in its row. */
  className?: string
}

function typingElsewhere(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return (
    target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT'
  )
}

/**
 * The search box of a list (spec-61 §3.6, generic port of the one of the dossier overview):
 * live, 300 ms debounce, and it follows the URL so back/forward shows the term again. `/`
 * focuses it; ⌘K stays free for the global search.
 */
export function SearchField({ value, onSearch, placeholder, label, className }: SearchFieldProps) {
  const [draft, setDraft] = useState(value)
  // `shown`: the URL term of the previous render, to notice a new one.
  // `sent`: the term this field last sent. It also follows every new URL term,
  // so back and then forward to a term sent earlier still counts as new.
  const [shown, setShown] = useState(value)
  const [sent, setSent] = useState(value)
  if (shown !== value) {
    setShown(value)
    setSent(value)
    // The field's own search coming back keeps what was typed: the URL has it
    // trimmed (a space before the next word), and react-router applies URL
    // changes in a transition, so keys pressed meanwhile are already in the
    // draft. Any other term (back/forward, a link) replaces the draft.
    if (value !== sent) setDraft(value)
  }
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const term = draft.trim()
    // One letter matches nearly everything and the backend ignores it (min 2).
    if (term === value || term.length === 1) return
    const timer = setTimeout(() => {
      setSent(term)
      onSearch(term)
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [draft, value, onSearch])

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || typingElsewhere(event.target)) {
        return
      }
      event.preventDefault()
      inputRef.current?.focus()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  return (
    <div className={cn('relative w-full min-w-56 md:max-w-sm', className)}>
      <Search
        className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <Input
        ref={inputRef}
        type="search"
        maxLength={200}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder={placeholder}
        aria-label={label}
        className="pl-8 max-md:h-11 max-md:text-base"
      />
    </div>
  )
}
